import { useEffect } from "react";

export const SITE = "https://c-styl.com";
export const APP_STORE_URL = "https://apps.apple.com/us/app/c-styl/id6811129106";

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export function useSeo({ title, description, path = "/", image = "/og-image.jpg", jsonLd = [] }) {
  useEffect(() => {
    const url = SITE + path;
    const img = image.startsWith("http") ? image : SITE + image;
    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:type", "website");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);
    upsertCanonical(url);
    window.scrollTo(0, 0);

    const nodes = (jsonLd || []).map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seo = "1";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    return () => nodes.forEach((n) => n.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, JSON.stringify(jsonLd)]);
}

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "C-STYL",
  url: SITE,
  logo: `${SITE}/logo.png`,
  sameAs: [APP_STORE_URL],
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "C-STYL",
  url: SITE,
};

export const SOFTWARE_APP_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "C-STYL",
  description:
    "C-STYL is an AI personal stylist, digital closet and virtual try-on app that styles outfits from the clothes you already own and lets you see them on your own body.",
  applicationCategory: "LifestyleApplication",
  operatingSystem: "iOS",
  url: SITE,
  downloadUrl: APP_STORE_URL,
  offers: [
    { "@type": "Offer", price: "5.99", priceCurrency: "USD", name: "C-STYL Pro Monthly" },
    { "@type": "Offer", price: "69.99", priceCurrency: "USD", name: "C-STYL Pro Annual" },
  ],
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: SITE + it.path,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
